
import { Card } from "@/components/ui/card";
import { StarIcon, ThumbsUp, Calendar, Award, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useEffect } from "react";

const Reviews = () => {
  const { toast } = useToast();
  const reviews = [
    {
      id: 1,
      projectTitle: "Website Redesign",
      clientName: "John Smith",
      rating: 5,
      comment: "Excellent work! The developer went above and beyond my expectations. They delivered the project ahead of schedule and implemented additional features that really enhanced the final product. Their communication was exceptional throughout the entire process.",
      date: "2024-03-15",
      verified: true,
      likes: 24,
      category: "Web Development",
      responses: 3,
    },
    {
      id: 2,
      projectTitle: "Mobile App Development",
      clientName: "Sarah Johnson",
      rating: 4,
      comment: "Great communication and professional work. The developer was very responsive and implemented all requested features efficiently. Would definitely work with them again on future projects.",
      date: "2024-03-10",
      verified: true,
      likes: 18,
      category: "Mobile Development",
      responses: 2,
    },
    {
      id: 3,
      projectTitle: "Logo Design",
      clientName: "Mike Wilson",
      rating: 5,
      comment: "Exceeded expectations with creative designs. The attention to detail and willingness to iterate based on feedback resulted in a perfect logo that perfectly represents our brand.",
      date: "2024-03-05",
      verified: false,
      likes: 31,
      category: "Design",
      responses: 5,
    },
    {
      id: 4,
      projectTitle: "E-commerce Platform",
      clientName: "Emily Chen",
      rating: 5,
      comment: "Outstanding development work on our e-commerce platform. The integration of crypto payments was seamless and the user interface is incredibly intuitive. Highly recommend!",
      date: "2024-03-01",
      verified: true,
      likes: 42,
      category: "E-commerce",
      responses: 7,
    },
    {
      id: 5,
      projectTitle: "Smart Contract Development",
      clientName: "Alex Thompson",
      rating: 5,
      comment: "Expert knowledge in blockchain development. Created secure and efficient smart contracts for our DeFi project. Documentation was thorough and code quality was excellent.",
      date: "2024-02-28",
      verified: true,
      likes: 56,
      category: "Blockchain",
      responses: 4,
    }
  ];

  useEffect(() => {
    toast({
      title: "Reviews Loaded",
      description: "Showing your latest client feedback and ratings",
      duration: 3000,
    });
  }, []);

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <StarIcon
        key={index}
        className={`w-4 h-4 ${
          index < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
        } transition-all duration-300 hover:scale-110`}
      />
    ));
  };

  const calculateOverallStats = () => {
    const totalReviews = reviews.length;
    const averageRating = reviews.reduce((acc, review) => acc + review.rating, 0) / totalReviews;
    const totalLikes = reviews.reduce((acc, review) => acc + review.likes, 0);
    return { totalReviews, averageRating, totalLikes };
  };

  const stats = calculateOverallStats();

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen bg-gradient-to-b from-purple-50/50 to-white">
      {/* Stats Overview */}
      <div className="mb-12">
        <h1 className="text-4xl font-bold mb-8 text-center bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
          My Reviews & Ratings
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-white shadow-md hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-4">
              <StarIcon className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-sm text-gray-600">Average Rating</p>
                <p className="text-2xl font-bold">{stats.averageRating.toFixed(1)}/5.0</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-white shadow-md hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-4">
              <MessageSquare className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-600">Total Reviews</p>
                <p className="text-2xl font-bold">{stats.totalReviews}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-emerald-50 to-white shadow-md hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-4">
              <ThumbsUp className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-sm text-gray-600">Total Likes</p>
                <p className="text-2xl font-bold">{stats.totalLikes}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Reviews List */}
      <div className="grid gap-6">
        {reviews.map((review) => (
          <Card 
            key={review.id} 
            className="p-6 hover:shadow-lg transition-all duration-300 animate-fade-in bg-gradient-to-br from-white to-gray-50/50"
          >
            <div className="space-y-4">
              <div className="flex justify-between items-start flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {review.verified && (
                      <Award className="w-5 h-5 text-blue-500" />
                    )}
                    <h3 className="text-xl font-semibold text-gray-900">{review.projectTitle}</h3>
                  </div>
                  <p className="text-gray-600 flex items-center gap-2">
                    <span>From: {review.clientName}</span>
                    {review.verified && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                        Verified Client
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-1">
                  {renderStars(review.rating)}
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 leading-relaxed">{review.comment}</p>
              </div>
              
              <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">
                      {new Date(review.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </span>
                  <span className="text-sm text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                    {review.category}
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1 text-gray-600">
                    <ThumbsUp className="w-4 h-4" />
                    <span className="text-sm">{review.likes}</span>
                  </span>
                  <span className="flex items-center gap-1 text-gray-600">
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm">{review.responses} responses</span>
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Reviews;

